import { useEffect, useState } from "react";
import Link from "next/link";
import { useAtomValue } from "jotai";
import { Box, Tooltip, Typography } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { ortexColorState, themeState } from "@/lib/store";
import { nFormatter } from "@/lib/utils";
import { getStockLockUpStatus, StockLockUpEntry } from "@/controllers/lock-ups";
import { formatCountdown, formatFloatShare } from "@/components/lock-ups/LockUpsSummary";

/**
 * "This company has shares coming free" — on the stock page, where it matters.
 *
 * A lock-up expiry is one of the few genuinely dated, genuinely quantified supply
 * events on a stock, and until now the page said nothing about it: the record was
 * in the Corporate Actions tab, under a Lock-ups filter, in a paragraph of CIQ
 * prose. Somebody reading a chart had no way to know that a quarter of the free
 * float becomes sellable on Thursday.
 *
 * Deliberately an absence most of the time. Around 750 companies have an expiry in
 * the next six months, so on the overwhelming majority of stock pages this renders
 * nothing at all — which is what makes it worth reading when it does appear.
 */

interface Props {
    /** `data.Company` id — a company's lock-ups belong to the company, not the listing. */
    companyId: number;
    /** For the link to the tab that holds the full record. */
    exchange?: string;
    ticker?: string;
    stockId?: number;
}

/**
 * How far out an expiry still earns a banner.
 *
 * The endpoint looks a year ahead so the page can answer "is there an overhang at
 * all", but a banner for something eleven months away is a banner people learn to
 * ignore. Six months matches the horizon the lock-ups page itself uses.
 */
const CALLOUT_HORIZON_DAYS = 180;

/** Inside this, the expiry is imminent enough to carry the accent border. */
const IMMINENT_DAYS = 30;

/** The one-line summary of what comes free. */
export const describeUnlock = (entry: StockLockUpEntry): string => {
    const parts = [
        entry.sharesLocked ? `${nFormatter(entry.sharesLocked, 1)} ${entry.securityClass || "shares"}` : entry.securityClass || "Restricted shares",
        entry.pctOfFreeFloat !== null ? formatFloatShare(entry.pctOfFreeFloat) : null,
        entry.daysOfVolume ? `${nFormatter(entry.daysOfVolume, 1)} days of average volume` : null,
        entry.valueUsd ? `$${nFormatter(entry.valueUsd, 1)}` : null,
    ].filter(Boolean);
    return parts.join(" · ");
};

/** "12 Sep 2026" in the user's locale, from the API's ISO date. */
export const formatExpiry = (iso: string | null): string => {
    if (!iso) return "";
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });
};

const StockLockupCallout = ({ companyId, exchange, ticker, stockId }: Props) => {
    const theme = useAtomValue(themeState);
    const ortexColor = useAtomValue(ortexColorState);
    const [entry, setEntry] = useState<StockLockUpEntry | null>(null);
    const [otherCount, setOtherCount] = useState(0);

    useEffect(() => {
        if (!companyId) return undefined;
        const controller = new AbortController();
        getStockLockUpStatus(companyId, controller.signal)
            .then((status) => {
                const next = status?.next;
                const withinHorizon = next?.daysUntil !== null && next?.daysUntil !== undefined && next.daysUntil <= CALLOUT_HORIZON_DAYS;
                setEntry(next && withinHorizon ? next : null);
                setOtherCount(Math.max((status?.upcoming?.length ?? 0) - 1, 0));
            })
            // Silent: the callout is extra context, and a stock page should not carry
            // an error box because an optional block could not load.
            .catch(() => setEntry(null));
        return () => controller.abort();
    }, [companyId]);

    if (!entry) return null;

    const isDark = theme === "dark";
    const imminent = (entry.daysUntil ?? 999) <= IMMINENT_DAYS;
    const border = imminent ? ortexColor : isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)";
    const href = exchange && ticker ? `/s/${exchange}/${ticker}/corporate-actions/all` : stockId ? `/id/${stockId}/corporate-actions` : null;

    const body = (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                padding: "10px 14px",
                borderRadius: "10px",
                marginBottom: "8px",
                border: `1px solid ${border}`,
                backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.015)",
                color: "inherit",
                textDecoration: "none",
                "&:hover .lockup-go": { opacity: 1 },
            }}
        >
            <LockOpenIcon sx={{ fontSize: 20, color: ortexColor, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.3 }}>
                    Lock-up expires {formatCountdown(entry.daysUntil)} , {formatExpiry(entry.expiryDate)}
                </Typography>
                {/*
                 * The parsed facts, not the CIQ paragraph they came from — that is
                 * still one click away on the tab, and on the tooltip here.
                 *
                 * Clamped to two lines rather than truncated to one: this block
                 * sits in a ~300px column on the overview, where a single ellipsed
                 * line lost the float share and the days of volume, which are the
                 * only part of the sentence that says whether the date matters.
                 */}
                <Tooltip title={entry.detail || ""}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {describeUnlock(entry)}
                        {entry.lockupTermDays ? ` · ${entry.lockupTermDays}-day lock-up` : ""}
                        {otherCount > 0 ? ` · +${otherCount} more ahead` : ""}
                    </Typography>
                </Tooltip>
            </Box>
            {href && <ArrowForwardIcon className="lockup-go" sx={{ fontSize: 16, opacity: 0.35, color: ortexColor, flexShrink: 0, transition: "opacity 0.15s ease" }} />}
        </Box>
    );

    if (!href) return body;

    return (
        <Link passHref legacyBehavior href={href}>
            <Box component="a" sx={{ textDecoration: "none", color: "inherit", display: "block" }}>
                {body}
            </Box>
        </Link>
    );
};

export default StockLockupCallout;
